import React, { useState, useEffect, useRef } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { apiFetch, formatUiErrors } from "utils";
import { useAuthState, useAuthDispatch } from "context";
import ShelterBuddyResyncForm from "./components/ShelterBuddyResyncForm";
import ShelterBuddyResyncStatus from "./components/ShelterBuddyResyncStatus";

const ShelterBuddyResync = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [steps, setSteps] = useState({
    animalData: "pending",
    pairing: "pending",
  });

  const animationTimeoutsRef = useRef([]);
  const pollingIntervalRef = useRef(null);
  const stepsRef = useRef(steps);
  const jobStatusRef = useRef(jobStatus);
  const currentJobIdRef = useRef(null);
  const hasShownCompletedToastForJobRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const lastProcessedJobIdRef = useRef(null);
  const completionToastTimeoutRef = useRef(null);

  const { mutate: startResync, isLoading: isStarting } = useMutation(
    (values) =>
      apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/shelterbuddy/resync",
        body: {
          date_from: values.dateFrom,
        },
      }),
    {
      onSuccess: (response) => {
        const jobData = response.data;

        if (jobData.jobId) {
          if (completionToastTimeoutRef.current) {
            clearTimeout(completionToastTimeoutRef.current);
            completionToastTimeoutRef.current = null;
          }

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          animationTimeoutsRef.current.forEach((timeout) =>
            clearTimeout(timeout)
          );
          animationTimeoutsRef.current = [];

          const newJobId = jobData.jobId;
          currentJobIdRef.current = newJobId;
          lastProcessedJobIdRef.current = null;
          setJobId(newJobId);
          setJobStatus("pending");
          jobStatusRef.current = "pending";
          setSteps({
            animalData: "pending",
            pairing: "pending",
          });
          stepsRef.current = {
            animalData: "pending",
            pairing: "pending",
          };

          hasShownCompletedToastForJobRef.current.delete(newJobId);

          toast.success("Resync process started successfully", {
            toastId: `resync-started-${newJobId}`,
          });

          startPolling(newJobId);
        } else {
          toast.error("Error: Could not retrieve Job ID");
        }
      },
      onError: (error) => {
        toast.error(() => formatUiErrors(error));
      },
    }
  );

  const simulateStepProgress = (stepKey, stepStatus) => {
    let delay = 0;

    if (stepStatus === "processing" || stepStatus === "running") {
      if (stepKey === "animalData") delay = 0;
      else if (stepKey === "pairing") delay = 1000;
    } else if (stepStatus === "completed") {
      if (stepKey === "animalData") delay = 0;
      else if (stepKey === "pairing") delay = 300;
    }

    return delay;
  };

  const showCompletionToast = (jobId) => {
    if (hasShownCompletedToastForJobRef.current.has(jobId)) return;

    hasShownCompletedToastForJobRef.current.add(jobId);

    if (completionToastTimeoutRef.current) {
      clearTimeout(completionToastTimeoutRef.current);
    }

    completionToastTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && currentJobIdRef.current === jobId) {
        toast.success("Resync completed successfully!", {
          toastId: `resync-completed-${jobId}`,
          autoClose: 5000,
        });
        completionToastTimeoutRef.current = null;
      }
    }, 800);
  };

  const animateSteps = (backendSteps, targetJobId, jobDataStatus) => {
    animationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    animationTimeoutsRef.current = [];

    const stepOrder = [
      { key: "animalData", label: "Animal Data" },
      { key: "pairing", label: "Pairing" },
    ];

    let maxDelay = 0;

    stepOrder.forEach(({ key }) => {
      const stepStatus = backendSteps[key] || "pending";
      const delay = simulateStepProgress(key, stepStatus);
      maxDelay = Math.max(maxDelay, delay);

      const timeout = setTimeout(() => {
        if (isMountedRef.current && currentJobIdRef.current === targetJobId) {
          setSteps((prev) => ({
            ...prev,
            [key]: stepStatus,
          }));
        }
      }, delay);

      animationTimeoutsRef.current.push(timeout);
    });

    if (jobDataStatus === "completed" && targetJobId) {
      const toastDelay = maxDelay + 300;
      const timeout = setTimeout(() => {
        if (isMountedRef.current && currentJobIdRef.current === targetJobId) {
          showCompletionToast(targetJobId);
        }
      }, toastDelay);

      animationTimeoutsRef.current.push(timeout);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    animationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    animationTimeoutsRef.current = [];
  };

  const startPolling = (id) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const fetchJobStatus = async () => {
      if (!isMountedRef.current) return;

      if (currentJobIdRef.current !== id) {
        stopPolling();
        return;
      }

      try {
        const response = await apiFetch({
          token,
          dispatch,
          method: "GET",
          endpoint: `/api/internal/shelterbuddy/resync/${id}`,
        });

        const jobData = response.data;

        if (jobData.status && jobData.status !== jobStatusRef.current) {
          setJobStatus(jobData.status);
          jobStatusRef.current = jobData.status;

          if (jobData.status === "failed") {
            if (
              currentJobIdRef.current === id &&
              !hasShownCompletedToastForJobRef.current.has(id)
            ) {
              hasShownCompletedToastForJobRef.current.add(id);
              toast.error(
                `Resync failed: ${jobData.error_message || "Unknown error"}`,
                { toastId: `resync-failed-${id}` }
              );
              stopPolling();
            }
          }
        }

        if (jobData.steps) {
          const normalizedSteps = {
            animalData:
              jobData.steps.animalData ||
              jobData.steps.animal_data ||
              "pending",
            pairing: jobData.steps.pairing || "pending",
          };

          const currentStepsStr = JSON.stringify(stepsRef.current);
          const newStepsStr = JSON.stringify(normalizedSteps);

          if (
            lastProcessedJobIdRef.current !== id ||
            currentStepsStr !== newStepsStr
          ) {
            lastProcessedJobIdRef.current = id;
            stepsRef.current = normalizedSteps;

            animateSteps(normalizedSteps, id, jobData.status);

            if (jobData.status === "completed") {
              setTimeout(() => {
                if (currentJobIdRef.current === id) {
                  stopPolling();
                }
              }, 2000);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching job status:", error);

        if (
          currentJobIdRef.current === id &&
          !hasShownCompletedToastForJobRef.current.has(id)
        ) {
          hasShownCompletedToastForJobRef.current.add(id);
          toast.error("Error fetching job status. Please try again.", {
            toastId: `resync-error-${id}`,
          });
          stopPolling();
        }
      }
    };

    fetchJobStatus();
    pollingIntervalRef.current = setInterval(fetchJobStatus, 2000);
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
      if (completionToastTimeoutRef.current) {
        clearTimeout(completionToastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (jobId && !pollingIntervalRef.current) {
      currentJobIdRef.current = jobId;
      startPolling(jobId);
    }
  }, [jobId]);

  const isProcessing = ["pending", "processing", "running"].includes(jobStatus);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">ShelterBuddy Data Resync</h1>

      <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800 border border-blue-100">
        This tool allows you to refresh your ShelterBuddy data. Select a "Date
        From" below, and we will resync animal data and update all records
        behind the scenes. Once finished, you can reprocess your monthly data.
      </div>

      <div className="bg-white rounded p-6 border shadow-sm max-w-3xl">
        <ShelterBuddyResyncForm
          onSubmit={startResync}
          isLoading={isStarting}
          isProcessing={isProcessing}
        />

        {jobId && (
          <ShelterBuddyResyncStatus steps={steps} jobStatus={jobStatus} />
        )}
      </div>
    </div>
  );
};

export default ShelterBuddyResync;
